/**
 * <mobile-media-gallery> — Swiper-based mobile product gallery that swaps the
 * main image when the selected variant changes. Self-initializing custom element.
 */
(function () {
  if (customElements.get('mobile-media-gallery')) return;

  // Variant ID -> image URL. Global lookup (cached with this asset).
  const VARIANT_IMAGE_MAP = new Map([
    ['45225774579910', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/WHITE_BASE.webp?v=1781799941'],
    ['45225774612678', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/BLACK_BASE.webp?v=1781799938'],
    ['44521880682694', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/White_4.png?v=1762433993'],
    ['44521880715462', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/White_8.png?v=1762433982'],
    ['44521880748230', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/White_12.png?v=1762433964'],
    ['51761666326824', 'https://www.heysilo.com/cdn/shop/files/White_24_v2.png?v=1762433823&width=1946'],
    ['44521883795654', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/Black_4.png?v=1762433933'],
    ['44521883828422', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/Black_8.png?v=1762433919'],
    ['44521883861190', 'https://cdn.shopify.com/s/files/1/0659/2705/3510/files/Black_12.png?v=1770202906'],
    ['45191207813318', 'https://www.heysilo.com/cdn/shop/files/Black_24.png?v=1762433879&width=1946'],
    ['44688322560198', 'https://cdn.shopify.com/s/files/1/0987/4744/9640/files/2whiteTallConteinrs.webp?v=1777552064'],
    ['44688322592966', 'https://cdn.shopify.com/s/files/1/0987/4744/9640/files/2whiteTallConteinrs.webp?v=1777552064'],
    ['44688322625734', 'https://cdn.shopify.com/s/files/1/0987/4744/9640/files/4whiteConteinrs.webp?v=1777552064'],
    ['44688322658502', 'https://cdn.shopify.com/s/files/1/0987/4744/9640/files/2blackShortConteinrs.webp?v=1777552064'],
    ['44688322691270', 'https://cdn.shopify.com/s/files/1/0987/4744/9640/files/2blackTallConteinrs.webp?v=1777552064'],
    ['44688322724038', 'https://cdn.shopify.com/s/files/1/0987/4744/9640/files/Container-B4-04_9e4c78ed-8853-4c4a-b6aa-497e2e6d0767.png?v=1777552054'],
  ]);

  const SWIPER_OPTIONS = {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    resistanceRatio: 0.85,
    touchRatio: 1,
    threshold: 10,
    followFinger: true,
    allowTouchMove: true,
  };

  const FADE_OUT_MS = 150;
  const FADE_IN_MS = 250;
  const DEBOUNCE_MS = 120;

  // Swiper loads from a separate deferred script, so it may not exist yet when
  // this element connects. Poll for it instead of giving up.
  const SWIPER_POLL_MS = 50;
  const SWIPER_POLL_MAX_MS = 5000;
  const MOBILE_MAX_WIDTH = 768;

  class MobileMediaGallery extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.img = this.wrapper?.querySelector('img');

      if (!this.wrapper || !this.img) {
        console.warn('[mobile-media-gallery] Missing gallery structure — init skipped.');
        return;
      }

      this.ensureSwiper();
      this.bindVariantListeners();
      this.updateMainImage();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('variant:updated', this.onVariantUpdated);
      this.observer?.disconnect();
      clearTimeout(this.debounceTimer);
      clearTimeout(this.swiperPollTimer);
    }

    // Wait until window.Swiper is available (it loads deferred), then init once.
    ensureSwiper(waited = 0) {
      if (this.swiper || window.innerWidth >= MOBILE_MAX_WIDTH) return;

      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
        return;
      }

      if (waited >= SWIPER_POLL_MAX_MS) {
        console.warn('[mobile-media-gallery] Swiper never loaded — slider disabled.');
        return;
      }

      this.swiperPollTimer = setTimeout(
        () => this.ensureSwiper(waited + SWIPER_POLL_MS),
        SWIPER_POLL_MS
      );
    }

    initSwiper() {
      if (this.swiper) return;

      this.swiper = new Swiper(this, {
        ...SWIPER_OPTIONS,
        pagination: { el: this.querySelector('.swiper-pagination'), clickable: true },
        on: {
          touchStart: () => { document.body.style.overscrollBehavior = 'none'; },
          touchEnd: () => { document.body.style.overscrollBehavior = ''; },
        },
      });

      window.mobileGallerySwiper = this.swiper;
    }

    getCurrentVariantId(fallbackId = null) {
      const fromUrl = new URLSearchParams(location.search).get('variant');
      if (fromUrl) return String(fromUrl);

      const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (idInput?.value) return String(idInput.value);

      return fallbackId ? String(fallbackId) : null;
    }

    updateMainImage(variantIdFromEvent = null) {
      const id = this.getCurrentVariantId(variantIdFromEvent);
      const imageUrl = VARIANT_IMAGE_MAP.get(String(id));
      if (!imageUrl || !this.img) return;

      // Preload, then crossfade so we never show a half-loaded swap.
      const preload = new Image();
      preload.src = imageUrl;
      preload.onload = () => {
        this.img.classList.add('fade-out');

        setTimeout(() => {
          this.img.src = imageUrl;
          this.img.alt = 'Variant Image';
          this.img.classList.remove('fade-out');
          this.img.classList.add('fade-in');

          setTimeout(() => this.img.classList.remove('fade-in'), FADE_IN_MS);
          this.swiper?.update?.();
        }, FADE_OUT_MS);
      };
    }

    bindVariantListeners() {
      this.onPopState = () => this.updateMainImage();
      this.onVariantUpdated = (e) => this.debounceUpdate(e?.detail?.variant?.id);

      window.addEventListener('popstate', this.onPopState);
      document.addEventListener('variant:updated', this.onVariantUpdated);

      // Catch-all for variant changes that don't dispatch `variant:updated`.
      this.observer = new MutationObserver(() => this.debounceUpdate());
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    debounceUpdate(id = null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.updateMainImage(id), DEBOUNCE_MS);
    }
  }

  customElements.define('mobile-media-gallery', MobileMediaGallery);
})();